import React from "react";
import { useNavigate } from "react-router-dom";
import { BsThermometerHalf } from "react-icons/bs";
import { ResponsiveContainer, AreaChart, Area } from "recharts";

import { useDashboard } from '../context/DashboardContext';

export default function TemperatureOverview() {
  const navigate = useNavigate();

  const { dashboardData, loading, errors } = useDashboard();
  const sensorCards = dashboardData.temperatureOverview?.sensorCards || ['Cold Storage', 'Fridges', 'Freezers'].map((title, id) => ({ id, title, currentTemp: '—', min: '—', max: '—', data: [] }));

  const handleViewAllSensors = () => {
    navigate("/temperature");
  };

  return (
    <div className="max-w-3xl w-full bg-white border border-slate-100 rounded-3xl p-6 shadow-sm font-sans text-slate-800">
      ```
      {/* CARD HEADER */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-bold text-slate-900">
          Temperature Overview
        </h2>

        <button
          type="button"
          onClick={handleViewAllSensors}
          className="text-[#3a31d8] text-xs font-bold hover:underline"
        >
          View All Sensors
        </button>
      </div>
      {/* HEALTH STATUS PILL */}
      <div className="flex items-center gap-1.5 text-green-600 font-semibold text-xs mb-5">
        <BsThermometerHalf className="text-base" />
        <span>{loading.temperatureOverview ? 'Loading…' : errors.temperatureOverview ? 'Unable to load temperatures' : dashboardData.temperatureOverview?.status || 'No readings'}</span>
      </div>
      {/* SENSOR SUB-CARDS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sensorCards.map((sensor) => (
          <div
            key={sensor.id}
            className="border border-slate-100 rounded-2xl p-4 flex flex-col justify-between bg-white shadow-[0_2px_8px_rgba(0,0,0,0.01)]"
          >
            {/* Metadata Text */}
            <div>
              <h3 className="text-xs font-bold text-slate-800 tracking-wide">
                {sensor.title}
              </h3>

              <div className="text-2xl font-bold text-slate-900 tracking-tight mt-1.5 mb-4">
                {sensor.currentTemp}
              </div>

              {/* Min / Max Specs */}
              <div className="space-y-1 text-[11px] font-medium text-slate-400">
                <div className="flex items-center justify-between max-w-[90px]">
                  <span>Min</span>
                  <span className="text-slate-600 font-semibold">
                    {sensor.min}
                  </span>
                </div>

                <div className="flex items-center justify-between max-w-[90px]">
                  <span>Max</span>
                  <span className="text-slate-600 font-semibold">
                    {sensor.max}
                  </span>
                </div>
              </div>
            </div>

            {/* Sparkline Graph Area */}
            <div className="w-full h-12 mt-6 -mx-4 -mb-4 overflow-hidden rounded-b-2xl">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={sensor.data}
                  margin={{ top: 2, right: 0, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id={`grad-${sensor.id}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="#3a31d8"
                        stopOpacity={0.15}
                      />
                      <stop offset="100%" stopColor="#3a31d8" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <Area
                    type="monotone"
                    dataKey="uv"
                    stroke="#2563eb"
                    strokeWidth={1.5}
                    fill={`url(#grad-${sensor.id})`}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
